import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "../lib/api";
import useSEO from "../lib/useSEO";
import FinalCTA from "../components/sections/FinalCTA";

function renderMarkdown(md = "") {
    // Minimal markdown: headings + paragraphs + bullets. Avoid heavy deps.
    const lines = md.split(/\n/);
    const out = [];
    let inList = false;
    let buf = [];
    const flush = () => {
        if (buf.length) {
            out.push(
                <p key={out.length} className="text-zinc-300 leading-relaxed mb-5">
                    {buf.join(" ")}
                </p>,
            );
            buf = [];
        }
    };
    lines.forEach((line, idx) => {
        if (/^##\s+/.test(line)) {
            flush();
            inList = false;
            out.push(
                <h2
                    key={`h-${idx}`}
                    className="font-display text-2xl md:text-3xl text-white mt-10 mb-4 tracking-tight"
                >
                    {line.replace(/^##\s+/, "")}
                </h2>,
            );
        } else if (/^-\s+/.test(line)) {
            flush();
            if (!inList) {
                out.push(<ul key={`ul-${idx}`} className="space-y-2 mb-6 list-disc list-inside text-zinc-300"></ul>);
                inList = true;
            }
            const lastUl = out[out.length - 1];
            const item = (
                <li key={`li-${idx}`} className="text-zinc-300 leading-relaxed">
                    {line.replace(/^-\s+/, "")}
                </li>
            );
            const items = lastUl.props.children
                ? Array.isArray(lastUl.props.children)
                    ? [...lastUl.props.children, item]
                    : [lastUl.props.children, item]
                : [item];
            out[out.length - 1] = (
                <ul key={lastUl.key} className={lastUl.props.className}>
                    {items}
                </ul>
            );
        } else if (line.trim() === "") {
            flush();
            inList = false;
        } else {
            inList = false;
            buf.push(line);
        }
    });
    flush();
    return out;
}

export default function BlogPost() {
    const { slug } = useParams();
    const [post, setPost] = useState(null);
    const [error, setError] = useState(false);

    useEffect(() => {
        api.get(`/blog/${slug}`)
            .then((r) => setPost(r.data))
            .catch(() => setError(true));
    }, [slug]);

    useSEO({
        title: post ? `${post.title} | İstanbul Dövme Silme` : "Yükleniyor...",
        description: post?.excerpt,
        canonical: `https://www.istanbuldovmesilme.com/blog/${slug}`,
    });

    if (error)
        return (
            <div className="min-h-[60vh] flex items-center justify-center text-center px-6">
                <div>
                    <p className="text-xl text-white mb-3">Yazı bulunamadı.</p>
                    <Link to="/blog" className="text-cyan-300 hover:underline">
                        ← Blog ana sayfa
                    </Link>
                </div>
            </div>
        );

    if (!post) return <p className="text-zinc-500 p-12">Yükleniyor...</p>;

    return (
        <>
            <article className="py-20 md:py-28">
                <div className="max-w-[820px] mx-auto px-6 md:px-12 lg:px-16">
                    <Link to="/blog" className="text-xs text-zinc-400 hover:text-cyan-300 mb-6 inline-block">
                        ← Tüm yazılar
                    </Link>
                    <h1 className="font-display text-4xl md:text-5xl text-white tracking-tight mb-4 text-balance">
                        {post.title}
                    </h1>
                    {post.excerpt && (
                        <p className="text-lg text-zinc-400 leading-relaxed mb-8">{post.excerpt}</p>
                    )}
                    {post.cover_image && (
                        <img
                            src={post.cover_image}
                            alt={post.title}
                            className="w-full rounded-2xl border border-white/5 mb-10"
                        />
                    )}
                    <div className="prose prose-invert max-w-none">{renderMarkdown(post.content)}</div>
                </div>
            </article>
            <FinalCTA />
        </>
    );
}
